/**
 * Viewport capture bridge — lets the MCP screenshot tool read the live
 * WebGL canvas. The active EngineViewport registers its canvas + the R3F
 * invalidate function + a synchronous `renderNow` hook here; the MCP router
 * forces a fresh render and reads the canvas INSIDE the same animation-frame
 * task, which is valid per the WebGL spec even without
 * `preserveDrawingBuffer` (the buffer is only cleared at frame presentation).
 */

let activeCanvas: HTMLCanvasElement | null = null;
let activeInvalidate: (() => void) | null = null;
let activeRenderNow: (() => void) | null = null;

/** Register (last-writer-wins) the active viewport for capture. */
export function registerViewportCapture(
    canvas: HTMLCanvasElement,
    invalidate: () => void,
    renderNow?: () => void
): void {
    activeCanvas = canvas;
    activeInvalidate = invalidate;
    activeRenderNow = renderNow ?? null;
}

/** Unregister only if the canvas is still the active one (multi-viewport safe). */
export function unregisterViewportCapture(canvas: HTMLCanvasElement): void {
    if (activeCanvas === canvas) {
        activeCanvas = null;
        activeInvalidate = null;
        activeRenderNow = null;
    }
}

export function isViewportCaptureAvailable(): boolean {
    return activeCanvas !== null;
}

/**
 * Force a fresh render pass, then grab the canvas PNG as base64 (without
 * the data-url prefix). Rejection when no viewport is mounted.
 *
 * The read happens in the same rAF task as the synchronous render
 * (`renderNow`), so the drawing buffer is guaranteed intact without
 * `preserveDrawingBuffer` — that flag costs a full-blit of the back buffer
 * on EVERY frame and used to be pinned on just for this capture path.
 */
export async function captureViewportPng(): Promise<string> {
    const canvas = activeCanvas;
    if (!canvas) throw new Error('No active 3D viewport canvas is mounted.');
    const renderNow = activeRenderNow;
    const base64 = await new Promise<string>((resolve, reject) => {
        requestAnimationFrame(() => {
            try {
                if (renderNow) {
                    renderNow();
                } else {
                    // Fallback for viewports without a synchronous render hook:
                    // invalidate + one extra frame (buffer stays readable).
                    activeInvalidate?.();
                    requestAnimationFrame(() => {
                        const u = canvas.toDataURL('image/png');
                        const c = u.indexOf(',');
                        c < 0 ? reject(new Error('Viewport capture produced no image data.')) : resolve(u.slice(c + 1));
                    });
                    return;
                }
                const u = canvas.toDataURL('image/png');
                const c = u.indexOf(',');
                c < 0 ? reject(new Error('Viewport capture produced no image data.')) : resolve(u.slice(c + 1));
            } catch (err) {
                reject(err instanceof Error ? err : new Error(String(err)));
            }
        });
    });
    return base64;
}
